import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import getEnv from '../utils/getEnv';

const { REACT_APP_AUTH_SERVICE_URL } = getEnv();

export interface User {
  auth0Identity?: string;
  email?: string;
  name?: {
    familyName: string;
    givenName: string;
  };
  anonymous: boolean;
  sessionID: string;
}

interface AuthProviderProps {
  children: ReactNode;
  applicationGroupId?: string;
}

export interface AuthContext {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  logout: () => Promise<void>;
  login: () => Promise<void>;
  error: unknown;
}

const AuthContext = createContext<AuthContext | undefined>(undefined);

export const useAuth = (): AuthContext => {
  const context = useContext(AuthContext);
  if (!context || context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({
  children,
  applicationGroupId
}: AuthProviderProps): JSX.Element => {
  const [isLoading, setisLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<unknown>();

  useEffect(() => {
    let isMounted = true; // Flag to track component mount status

    const getAuthInfo = async () => {
      setisLoading(true);
      try {
        const res = await fetch(`${REACT_APP_AUTH_SERVICE_URL}/me`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            applicationGroupId: applicationGroupId || ''
          }
        });
        const result = await res.json();
        if (isMounted) {
          if (
            result &&
            typeof result === 'object' &&
            !Array.isArray(result) &&
            Object.keys(result).length !== 0
          ) {
            result.email_verified =
              !result?.anonymous && (result?.email?.length ?? 0) > 0;
            setUser(result);
          }
          setisLoading(false);
        }
      } catch (error) {
        if (isMounted) {
          setError(error);
          setisLoading(false);
        }
      } finally {
        if (isMounted) {
          setisLoading(false);
        }
      }
    };

    getAuthInfo();

    return () => {
      isMounted = false; // Cleanup function sets the flag to false on unmount
    };
  }, [applicationGroupId]);

  const logout = async () => {
    window.location.href = `${REACT_APP_AUTH_SERVICE_URL}/logout`;
  };

  const login = async () => {
    window.location.href = `${REACT_APP_AUTH_SERVICE_URL}/auth0/login?redirectTo=${window.location.origin}`;
  };
  const value = useMemo(
    () => ({
      user,
      isAuthenticated: !!user && !user.anonymous,
      isLoading,
      logout,
      login,
      error
    }),
    [user, isLoading, error]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
